// KELTA Group Health Insurance — member data collection page.
// Loads campaign + published form schema + the member's own submission.
// Core identity fields (KELTA ID, name, designation, local body, district,
// mobile, email) are fetched from the member profile and shown read-only.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FieldRenderer, { isVisible, calcAge } from "@/components/insurance/FieldRenderer";
import RepeaterField, { PersonsSummary } from "@/components/insurance/RepeaterField";
import {
  ShieldCheck, Loader2, Save, Send, AlertTriangle, CheckCircle2, HeartPulse,
  Lock, CalendarClock, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_UI = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  submitted: { label: "Submitted", cls: "bg-blue-50 text-blue-700 border-blue-300" },
  under_review: { label: "Under Review", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  data_verified: { label: "Data Verified", cls: "bg-emerald-50 text-teal-600 border-emerald-300" },
  correction_required: { label: "Correction Required", cls: "bg-red-50 text-red-700 border-red-300" },
  sent_to_insurance: { label: "Sent to Insurance Company", cls: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-teal-700 border-emerald-300" },
};

const READONLY_IDENTITY = [
  ["kelta_id", "KELTA ID"], ["name", "Name of Employee / KELTA Member"], ["designation", "Designation"],
  ["local_body", "Local Body"], ["district", "District"], ["mobile", "Mobile Number"], ["email", "Email ID"],
];

export default function InsurancePage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [boot, setBoot] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [answers, setAnswers] = useState({});
  const [persons, setPersons] = useState([]);
  const [busy, setBusy] = useState("");
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login?next=/insurance"); return; }
    api.get("/insurance/boot")
      .then(r => {
        setBoot(r.data);
        const sub = r.data.submission;
        if (sub) {
          setAnswers(sub.answers || {});
          setPersons(sub.insured_persons || []);
        }
      })
      .catch(e => setLoadErr(e?.response?.data?.detail || "Could not load the Group Insurance form"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const campaign = boot?.campaign;
  const schema = boot?.form?.schema;
  const sub = boot?.submission;
  const status = sub?.status || null;
  const st = status ? STATUS_UI[status] : null;

  // editable when: no submission | draft | correction_required | (submitted-ish + admin allows edit)
  const editable = !sub
    || status === "draft"
    || status === "correction_required"
    || (["submitted", "under_review", "data_verified"].includes(status) && campaign?.allow_edit_after_submit);
  const lockedFinal = status === "sent_to_insurance" || status === "completed";
  const open = campaign?.submissions_open;

  const setA = (k, v) => setAnswers(a => ({ ...a, [k]: v }));

  const visibleFields = (sec) => (sec.fields || []).filter(f => f.enabled !== false && isVisible(f, answers));

  const validate = () => {
    for (const sec of schema.sections) {
      if (sec.enabled === false) continue;
      for (const f of visibleFields(sec)) {
        if (["heading", "description", "divider", "insured_repeater"].includes(f.type)) continue;
        const v = answers[f.key];
        const empty = v === undefined || v === "" || v === null || (Array.isArray(v) && !v.length);
        if (f.required && empty) return `Required: ${f.label}`;
      }
    }
    for (let i = 0; i < persons.length; i++) {
      const p = persons[i];
      if (!p.name?.trim() || !p.relationship || !p.dob || !p.gender) return `Insured Person #${i + 1}: name, relationship, date of birth and gender are all required`;
    }
    return null;
  };

  const save = async (final) => {
    const tid = toast.loading(final ? "Submitting…" : "Saving draft…", { id: "gi-save" });
    setBusy(final ? "submit" : "draft");
    try {
      const payload = { answers, insured_persons: persons };
      const url = final ? "/insurance/my/submit" : "/insurance/my/draft";
      const { data } = final ? await api.post(url, payload) : await api.put(url, payload);
      setBoot(b => ({ ...b, submission: data }));
      toast.success(final ? `Submitted — Reference No ${data.ref_no}` : "Draft saved — you can continue later", { id: tid });
      if (final) window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed", { id: tid });
    } finally { setBusy(""); }
  };

  const submitClick = () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!showReview) { setShowReview(true); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); return; }
    save(true);
  };

  if (loadErr) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-600 mb-3" />
          <p className="text-muted-foreground">{loadErr}</p>
        </div><Footer /></div>
    );
  }
  if (!boot) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading Group Insurance form…
        </div><Footer /></div>
    );
  }

  const m = boot.member;

  return (
    <div className="min-h-screen bg-background" data-testid="insurance-page">
      <Navbar />
      {/* campaign banner */}
      <section className="relative overflow-hidden text-white" style={{ background: "linear-gradient(135deg, #04202c 0%, #065a66 45%, #0d8a96 100%)" }}>
        {campaign.banner_image && (
          <img src={campaign.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex items-center gap-2 text-cyan-300 text-xs font-bold uppercase tracking-[0.2em]">
            <HeartPulse className="w-4 h-4" /> KELTA Welfare Initiative
          </div>
          <h1 className="text-2xl sm:text-4xl font-black mt-2">
            {campaign.heading}
          </h1>
          <p className="text-cyan-50/85 mt-2 max-w-2xl text-sm sm:text-base">{campaign.description}</p>
          <div className="flex flex-wrap items-center gap-3 mt-4 text-xs">
            {campaign.last_date && (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold ${campaign.closed ? "bg-red-500/20 text-red-200" : "bg-white/10 text-cyan-200"}`} data-testid="gi-deadline">
                <CalendarClock className="w-3.5 h-3.5" />
                {campaign.closed ? "Data Collection Closed" : `Last date: ${campaign.last_date.split("-").reverse().join("-")}`}
              </span>
            )}
            {sub && (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-semibold bg-white ${st.cls}`} data-testid="gi-status-badge">
                {st.label} · {sub.ref_no}
              </span>
            )}
          </div>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* correction / closed / locked banners */}
        {status === "correction_required" && (
          <Card className="p-4 border-red-300 bg-red-50 flex gap-3" data-testid="gi-correction-banner">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-800 text-sm">Correction requested by KELTA Admin</div>
              <p className="text-sm text-red-700 mt-0.5">{sub.correction_note}</p>
              <p className="text-xs text-red-600 mt-1">Please correct the information below and resubmit. Your reference number stays the same.</p>
            </div>
          </Card>
        )}
        {campaign.closed && !sub && (
          <Card className="p-4 border-amber-300 bg-amber-50 text-sm text-amber-800" data-testid="gi-closed-banner">
            Group Insurance data collection is <strong>closed</strong> (last date passed). Please contact KELTA admin if you missed the deadline.
          </Card>
        )}
        {lockedFinal && (
          <Card className="p-4 border-emerald-300 bg-emerald-50 flex gap-3" data-testid="gi-locked-banner">
            <Lock className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
            <div className="text-sm text-teal-700">Your submission <strong>{sub.ref_no}</strong> is <strong>{st.label}</strong> and can no longer be edited.</div>
          </Card>
        )}
        {status === "submitted" && !campaign.allow_edit_after_submit && (
          <Card className="p-4 border-blue-200 bg-blue-50 flex gap-3" data-testid="gi-submitted-banner">
            <CheckCircle2 className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">Submitted successfully. Reference No: <strong className="font-mono">{sub.ref_no}</strong>. KELTA will review your data and contact you if needed.</div>
          </Card>
        )}

        {/* member identity (read-only) */}
        <Card className="p-5" data-testid="gi-identity">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-teal-700" />
            <h2 className="font-bold text-base">Your KELTA Information</h2>
            <span className="text-[10px] text-muted-foreground">(fetched automatically — cannot be edited here)</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {READONLY_IDENTITY.map(([k, label]) => (
              <div key={k} className="rounded-md bg-muted/50 border border-border px-3 py-2" data-testid={`gi-id-${k}`}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="text-sm font-semibold truncate">{m[k] || "—"}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* the schema-driven form */}
        {open || sub ? (
          schema.sections.filter(s => s.enabled !== false).map(sec => {
            const fields = visibleFields(sec);
            if (!fields.length) return null;
            return (
              <Card key={sec.id} className="p-5" data-testid={`gi-sec-${sec.key}`}>
                <h2 className="font-bold text-base text-teal-900">
                  {sec.title_en || sec.title_ml}
                </h2>
                {sec.description && <p className="text-xs text-muted-foreground mt-0.5 mb-3">{sec.description}</p>}
                <div className="space-y-4 mt-3">
                  {fields.map(f => f.type === "insured_repeater" ? (
                    <RepeaterField key={f.id} field={f} value={persons} onChange={setPersons} readonly={!editable} testid="gi-persons" />
                  ) : (
                    <FieldRenderer key={f.id} field={f} value={answers[f.key]} onChange={v => setA(f.key, v)} answers={answers} readonly={!editable} testid={`gi-f-${f.key}`} />
                  ))}
                </div>
              </Card>
            );
          })
        ) : null}

        {/* review + actions */}
        {editable && open !== false && (
          <Card className="p-5" data-testid="gi-actions">
            <button type="button" className="flex items-center gap-2 font-bold text-base w-full text-left" onClick={() => setShowReview(v => !v)} data-testid="gi-review-toggle">
              {showReview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Review before submitting — {1 + persons.length} person(s) proposed
            </button>
            {showReview && (
              <div className="mt-4 space-y-4">
                <PersonsSummary persons={persons} member={{ ...m, name: m.name, dob: m.dob, gender: m.gender }} />
                <div className="text-xs text-muted-foreground space-y-1" data-testid="gi-answers-summary">
                  {schema.sections.filter(s => s.enabled !== false).flatMap(s => visibleFields(s)).filter(f => !["heading", "description", "divider", "insured_repeater", "checkbox", "file_upload", "image_upload", "signature", "table"].includes(f.type)).map(f => (
                    answers[f.key] !== undefined && answers[f.key] !== "" ? (
                      <div key={f.id}><span className="font-semibold">{f.label}:</span> {Array.isArray(answers[f.key]) ? answers[f.key].join(", ") : String(answers[f.key])}</div>
                    ) : null
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-5">
              <Button variant="outline" onClick={() => save(false)} disabled={!!busy} data-testid="gi-save-draft">
                {busy === "draft" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save as Draft
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={submitClick} disabled={!!busy} data-testid="gi-submit">
                {busy === "submit" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                {showReview ? "Submit Final" : "Review & Submit"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Submission of this form does not constitute confirmation of insurance coverage.</p>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
